import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Terminal,
  Shield,
  GitCompare,
  DownloadCloud,
  Route as RouteIcon,
  Clock,
  LogOut,
  LogIn,
  UserCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const items = [
  { to: "/", label: "Analysis", icon: Shield, testId: "nav-inspect", public: true },
  { to: "/diff", label: "Compare", icon: GitCompare, testId: "nav-diff", public: true },
  { to: "/import", label: "Import", icon: DownloadCloud, testId: "nav-import", public: true },
  { to: "/traffic", label: "Simulation", icon: RouteIcon, testId: "nav-traffic", public: true },
  { to: "/history", label: "History", icon: Clock, testId: "nav-history", public: false },
];

export const TopNav = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, config, logout } = useAuth();
  const authEnabled = !!config?.enabled;
  const signedIn = !!user;

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Signed out");
      nav("/");
    } catch {
      toast.error("Logout failed");
    }
  };

  return (
    <nav className="border-b border-[#1e293b] bg-[#020617]/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-blue-400 font-mono text-[11px] tracking-[0.3em] uppercase shrink-0">
          <Terminal className="h-3.5 w-3.5" />
          <span>TOOLS HUB</span>
          <span className="text-slate-600 mx-1">·</span>
          <span className="text-slate-500">v3.0</span>
        </div>

        <div className="flex items-center gap-1 flex-wrap justify-end">
          {items
            .filter((it) => it.public || authEnabled)
            .map((it) => {
              const active = loc.pathname === it.to;
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.testId}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs font-mono uppercase tracking-[0.15em]",
                    active
                      ? "border-blue-500/50 bg-blue-500/10 text-blue-300"
                      : "border-transparent text-slate-400 hover:text-slate-100 hover:border-[#1e293b] hover:bg-[#0f172a]"
                  )}
                  style={{
                    transition:
                      "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                  }}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {it.label}
                </NavLink>
              );
            })}

          {authEnabled && (
            <div className="ml-2 flex items-center gap-2 pl-3 border-l border-[#1e293b]">
              {signedIn ? (
                <>
                  <div className="hidden md:flex items-center gap-2 text-xs font-mono text-slate-400">
                    <UserCircle2 className="h-4 w-4 text-slate-500" />
                    <span data-testid="nav-user-email" className="text-slate-200">
                      {user.email}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    data-testid="nav-logout-btn"
                    className="h-8 px-2 rounded-sm text-slate-400 hover:text-slate-100 hover:bg-[#1e293b]"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => nav("/login")}
                  data-testid="nav-signin-btn"
                  className="h-8 px-3 rounded-sm bg-blue-500 hover:bg-blue-400 text-white text-xs font-mono uppercase tracking-[0.15em]"
                >
                  <LogIn className="h-3.5 w-3.5 mr-2" />
                  Sign in
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
